import { useI18n } from 'vue-i18n';
import { useFeature } from '@/composables/use-feature';

export function useWorkpackageScopeTooltipOptions() {
  const { previousWorkpackageLinkingEnabled } = useFeature();
  const { t } = useI18n();
  const autoPopulateTooltipSections = [
    {
      tips: [
        {
          text: t('tooltips.autoPopulate.intro'),
          nestedTips: [
            t('tooltips.autoPopulate.setsDates'),
            t('tooltips.autoPopulate.setsAssortmentGroups'),
            ...(previousWorkpackageLinkingEnabled?.value
              ? [t('tooltips.autoPopulate.setsPreviousChainedWorkpackage')]
              : []),
          ],
        },
      ],
    },
  ];
  const importFromAssortmentGroupsToolTipSections = [
    {
      tips: [
        {
          text: t('tooltips.importFromAssortmentGroups.thisFunctionWill'),
          nestedTips: [
            t('tooltips.importFromAssortmentGroups.stores'),
            t('tooltips.importFromAssortmentGroups.productsFromSnapshot'),
            t('tooltips.importFromAssortmentGroups.productsFromTimespan'),
          ],
        },
      ],
    },
  ];

  const snapshotDateTooltipSections = [
    {
      tips: [t('tooltips.snapshotDate.info'), t('tooltips.snapshotDate.defines')],
    },
    { tips: [t('tooltips.snapshotDate.changeSetting')] },
  ];

  const tnapshotDateTooltipSections = [
    {
      tips: [t('tooltips.snapshotDate.info'), t('tooltips.snapshotDate.defines')],
    },
    { tips: [t('tooltips.snapshotDate.changeSetting')] },
  ];

  const switchingPeriodTooltipSections = [
    {
      tips: [
        t('tooltips.switchingPeriod.indicate'),
        t('tooltips.switchingPeriod.switchingRequiresData'),
      ],
    },
    { tips: [t('tooltips.switchingPeriod.implications')] },
    { tips: [t('tooltips.switchingPeriod.changeSetting')] },
  ];

  const learningPeriodTooltipSections = [
    {
      tips: [
        t('tooltips.learningPeriod.indicate'),
        t('tooltips.learningPeriod.weeksCanBeExcluded'),
      ],
    },
    { tips: [t('tooltips.learningPeriod.implications')] },
    { tips: [t('tooltips.learningPeriod.changeSetting')] },
  ];

  const fillAccordingToTooltipSections = [
    {
      tips: [
        t('tooltips.fillAccordingTo.indicate'),
        {
          type: 'icons',
          text: t('tooltips.fillAccordingTo.shouldMatchPlanograms'),
          nestedTips: [
            {
              icon: '$linear-space',
              text: t('tooltips.fillAccordingTo.linearSpace'),
            },
            {
              icon: '$cubic-space',
              text: t('tooltips.fillAccordingTo.cubicSpace'),
            },
            {
              icon: '$frontal-space',
              text: t('tooltips.fillAccordingTo.frontalSpace'),
            },
            {
              icon: '$horizontal-space',
              text: t('tooltips.fillAccordingTo.horizontalSpace'),
            },
            {
              icon: '$empty-cube',
              text: t('tooltips.fillAccordingTo.productCount'),
            },
          ],
        },
      ],
    },
    {
      tips: [
        t('tooltips.fillAccordingTo.settingYouChoose'),
        t('tooltips.fillAccordingTo.note'),
        t('tooltips.fillAccordingTo.ifWishToChange'),
        t('tooltips.fillAccordingTo.ifHierarchyAssortmentGroup'),
      ],
    },
  ];

  const targetLaunchDateTooltipSections = [
    { tips: [t('tooltips.targetLaunchDate.info'), t('tooltips.targetLaunchDate.indicate')] },
  ];
  const performancePeriodTooltipSections = [
    {
      tips: [
        t('tooltips.performancePeriod.indicate'),
        t('tooltips.performancePeriod.weeksCanBeExcluded'),
      ],
    },
    { tips: [t('tooltips.performancePeriod.implications')] },
    { tips: [t('tooltips.performancePeriod.changeSetting')] },
  ];

  const previousWorkpackageTooltipSections = [
    { tips: [t('tooltips.previousWorkpackage.indicate')] },
  ];
  const previousScenarioTooltipSections = [
    {
      tips: [t('tooltips.previousScenario.indicate'), t('tooltips.previousScenario.implications')],
    },
  ];

  return {
    autoPopulateTooltipSections,
    importFromAssortmentGroupsToolTipSections,
    snapshotDateTooltipSections,
    switchingPeriodTooltipSections,
    learningPeriodTooltipSections,
    fillAccordingToTooltipSections,
    targetLaunchDateTooltipSections,
    performancePeriodTooltipSections,
    tnapshotDateTooltipSections,
    previousWorkpackageTooltipSections,
    previousScenarioTooltipSections,
  };
}

export function useWorkpackageProductsTooltipOptions() {
  const { t } = useI18n();

  const productScopeTooltipSections = [
    {
      tips: [
        {
          text: t('tooltips.productScope.defineProducts'),
          nestedTips: [
            t('tooltips.productScope.soldProducts'),
            t('tooltips.productScope.soldInactiveProducts'),
          ],
        },
        t('tooltips.productScope.addingExistingProducts'),
        t('tooltips.productScope.newProducts'),
      ],
    },
    { tips: [t('tooltips.productScope.implications')] },
    { tips: [t('tooltips.productScope.changeSetting')] },
  ];

  return { productScopeTooltipSections };
}

export function useWorkpackageStoresTooltipOptions() {
  const { t } = useI18n();

  const storeScopeTooltipSections = [
    {
      tips: [
        t('tooltips.storeScope.indicate'),
        t('tooltips.storeScope.forAnalysis'),
        t('tooltips.storeScope.forAssortment'),
        t('tooltips.storeScope.analysisNotAssortment'),
        t('tooltips.storeScope.assortmentNotAnalysis'),
      ],
    },
    {
      tips: [
        {
          text: t('tooltips.storeScope.mayBeIncluded'),
          nestedTips: [
            t('tooltips.storeScope.includedAnalysis'),
            t('tooltips.storeScope.includedAssortment'),
          ],
        },
      ],
    },
  ];
  return { storeScopeTooltipSections };
}

export function useFilterSplitWorkpackages() {
  const { t } = useI18n();
  const filterSplitWorkpackagesSections = [
    { tips: [t('tooltips.filterSplitWorkpackages.helpContent')] },
  ];
  return { filterSplitWorkpackagesSections };
}

export function useScenarioOptimiserTooltipOptions() {
  const { t } = useI18n();
  const optimiseAccordingToTooltipSections = [
    {
      tips: [
        t('tooltips.optimiseAccordingTo.indicate'),
        {
          type: 'icons',
          text: t('tooltips.optimiseAccordingTo.shouldBeAligned'),
          nestedTips: [
            {
              icon: '$linear-space',
              text: t('tooltips.optimiseAccordingTo.linearSpace'),
            },
            {
              icon: '$cubic-space',
              text: t('tooltips.optimiseAccordingTo.cubicSpace'),
            },
            {
              icon: '$frontal-space',
              text: t('tooltips.optimiseAccordingTo.frontalSpace'),
            },
            {
              icon: '$horizontal-space',
              text: t('tooltips.optimiseAccordingTo.horizontalSpace'),
            },
            {
              icon: '$empty-cube',
              text: t('tooltips.optimiseAccordingTo.productCount'),
            },
          ],
        },
        t('tooltips.optimiseAccordingTo.used'),
      ],
    },
  ];
  const categoryStrategyTooltipSections = [
    {
      tips: [t('tooltips.categoryStrategy.indicate'), t('tooltips.categoryStrategy.implications')],
    },
  ];
  const marginWithoutFundingSections = [
    { tips: [t('tooltips.optimiseAccordingTo.marginWithoutFundingWarning')] },
  ];

  return {
    optimiseAccordingToTooltipSections,
    categoryStrategyTooltipSections,
    marginWithoutFundingSections,
  };
}
